import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  AcademicCapIcon,
  EyeIcon,
  EyeSlashIcon,
  Cog6ToothIcon,
  GlobeAltIcon,
  LockClosedIcon,
  CurrencyEuroIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline'
import { Card, Badge, InlineSpinner, EmptyState, Button, Modal, ModalBody, ModalFooter } from '@/components/ui'
import { lmsApi } from '@/api'
import type { AdminCourse, AdminLmsCategory, CertificateTemplate, CreateCourseData } from '@/api/lms'

const levelOptions = [
  { value: 'basic', label: 'Base' },
  { value: 'beginner', label: 'Principiante' },
  { value: 'intermediate', label: 'Intermedio' },
  { value: 'advanced', label: 'Avanzato' },
]

const emptyForm: CreateCourseData = {
  title: '',
  slug: '',
  description: '',
  short_description: '',
  category: null,
  level: 'basic',
  duration_hours: 1,
  is_premium: false,
  is_published: false,
  has_certificate: true,
  certificate_model: null,
  sequential_modules: false,
  is_public: false,
  is_free: true,
}

export default function AdminCoursesPage() {
  const navigate = useNavigate()
  const [courses, setCourses] = useState<AdminCourse[]>([])
  const [categories, setCategories] = useState<AdminLmsCategory[]>([])
  const [certificateTemplates, setCertificateTemplates] = useState<CertificateTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  // Create/Edit Modal
  const [showModal, setShowModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState<AdminCourse | null>(null)
  const [form, setForm] = useState<CreateCourseData>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [courseToDelete, setCourseToDelete] = useState<AdminCourse | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [coursesData, categoriesData] = await Promise.all([
        lmsApi.admin.getCourses(),
        lmsApi.admin.getCategories(),
      ])
      setCourses(coursesData)
      setCategories(categoriesData)
      // The template list needs course-editing rights: without them the course form still works
      lmsApi.admin.getCertificateTemplates().then(setCertificateTemplates).catch(() => undefined)
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
  }

  const handleTitleChange = (title: string) => {
    setForm({
      ...form,
      title,
      slug: isEditing ? form.slug : generateSlug(title),
    })
  }

  const handleOpenCreate = () => {
    setIsEditing(false)
    setSelectedCourse(null)
    setForm(emptyForm)
    setError(null)
    setShowModal(true)
  }

  const openCourseEditModal = (course: AdminCourse) => {
    // FIXED: Now populates description and short_description
    setIsEditing(true)
    setSelectedCourse(course)
    setForm({
      title: course.title,
      slug: course.slug,
      description: course.description || '',
      short_description: course.short_description || '',
      category: course.category,
      level: course.level as CreateCourseData['level'],
      duration_hours: course.duration_hours,
      is_premium: course.is_premium,
      is_published: course.is_published,
      has_certificate: course.has_certificate ?? true,
      certificate_model: course.certificate_model ?? null,
      sequential_modules: course.sequential_modules ?? false,
      is_public: course.is_public ?? false,
      is_free: course.is_free ?? true,
    })
    setError(null)
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Il titolo è obbligatorio')
      return
    }
    if (!form.slug.trim()) {
      setError('Lo slug è obbligatorio')
      return
    }

    setSaving(true)
    setError(null)

    try {
      if (isEditing && selectedCourse) {
        await lmsApi.admin.updateCourse(selectedCourse.id, form)
      } else {
        await lmsApi.admin.createCourse(form)
      }
      setShowModal(false)
      setForm(emptyForm)
      fetchData()
    } catch (err: unknown) {
      const error = err as { response?: { data?: Record<string, string[]> } }
      if (error.response?.data) {
        const messages = Object.values(error.response.data).flat()
        setError(messages.join(', '))
      } else {
        setError('Errore durante il salvataggio')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!courseToDelete) return
    setDeleting(true)
    try {
      await lmsApi.admin.deleteCourse(courseToDelete.id)
      setShowDeleteModal(false)
      setCourseToDelete(null)
      fetchData()
    } catch (error) {
      console.error('Error deleting course:', error)
    } finally {
      setDeleting(false)
    }
  }

  const handleTogglePublish = async (course: AdminCourse) => {
    try {
      await lmsApi.admin.togglePublish(course.id)
      fetchData()
    } catch (error) {
      console.error('Error toggling publish:', error)
    }
  }

  const filteredCourses = courses.filter(
    (course) =>
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.slug.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const getLevelBadge = (level: string) => {
    const config: Record<string, { variant: 'success' | 'warning' | 'danger'; label: string }> = {
      basic: { variant: 'success', label: 'Base' },
      beginner: { variant: 'success', label: 'Base' },
      intermediate: { variant: 'warning', label: 'Intermedio' },
      advanced: { variant: 'danger', label: 'Avanzato' },
    }
    return config[level] || { variant: 'success' as const, label: level }
  }

  if (loading) {
    return <InlineSpinner message="Caricamento corsi..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Corsi</h1>
          <p className="text-gray-600">{courses.length} corsi configurati</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/gestione/attestati')}>
            <CheckBadgeIcon className="h-5 w-5 mr-2" />
            Modelli attestato
          </Button>
          <Button onClick={handleOpenCreate}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Nuovo Corso
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca per titolo o slug..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </Card>

      {/* Courses Table */}
      {filteredCourses.length > 0 ? (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Corso
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Categoria
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statistiche
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredCourses.map((course) => (
                  <tr key={course.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                          <AcademicCapIcon className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                          <p
                            className="font-medium text-gray-900 hover:text-primary-600 cursor-pointer"
                            onClick={() => navigate(`/gestione/courses/${course.id}`)}
                          >
                            {course.title}
                          </p>
                          <code className="text-xs text-gray-500">{course.slug}</code>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {course.category_name || 'Nessuna'}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      <div>{course.modules_count} moduli</div>
                      <div>{course.lessons_count} lezioni</div>
                      <div>{course.enrolled_count} iscritti</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col gap-1">
                        {course.is_published ? (
                          <Badge variant="success">Pubblicato</Badge>
                        ) : (
                          <Badge variant="default">Bozza</Badge>
                        )}
                        <Badge variant={getLevelBadge(course.level).variant}>
                          {getLevelBadge(course.level).label}
                        </Badge>
                        {course.is_public ? (
                          <Badge variant="info">
                            <GlobeAltIcon className="h-3 w-3 mr-1" />
                            Pubblico
                          </Badge>
                        ) : (
                          <Badge variant="default">
                            <LockClosedIcon className="h-3 w-3 mr-1" />
                            Privato
                          </Badge>
                        )}
                        {course.is_free ? (
                          <Badge variant="success">
                            <CheckBadgeIcon className="h-3 w-3 mr-1" />
                            Gratuito
                          </Badge>
                        ) : (
                          <Badge variant="warning">
                            <CurrencyEuroIcon className="h-3 w-3 mr-1" />
                            A pagamento
                          </Badge>
                        )}
                        {course.is_premium && (
                          <Badge variant="warning">Premium</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/gestione/courses/${course.id}`)}
                          className="p-1 text-primary-600 hover:bg-primary-50 rounded"
                          title="Gestisci Contenuti"
                        >
                          <Cog6ToothIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleTogglePublish(course)}
                          className={`p-1 rounded ${
                            course.is_published
                              ? 'text-gray-500 hover:bg-gray-100'
                              : 'text-green-500 hover:bg-green-50'
                          }`}
                          title={course.is_published ? 'Nascondi' : 'Pubblica'}
                        >
                          {course.is_published ? (
                            <EyeSlashIcon className="h-5 w-5" />
                          ) : (
                            <EyeIcon className="h-5 w-5" />
                          )}
                        </button>
                        <button
                          onClick={() => openCourseEditModal(course)}
                          className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                          title="Modifica"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            setCourseToDelete(course)
                            setShowDeleteModal(true)
                          }}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                          title="Elimina"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card>
          <EmptyState
            icon={<AcademicCapIcon className="h-12 w-12" />}
            title={searchQuery ? 'Nessun corso trovato' : 'Nessun corso'}
            description={
              searchQuery
                ? 'Prova a modificare i termini di ricerca'
                : 'Crea il primo corso per la piattaforma'
            }
            action={
              !searchQuery
                ? { label: 'Crea Corso', onClick: handleOpenCreate }
                : undefined
            }
          />
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={isEditing ? 'Modifica Corso' : 'Nuovo Corso'}
      >
        <ModalBody>
          <div className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titolo *
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="es. Introduzione alla Compliance"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Slug *
              </label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="es. introduzione-compliance"
              />
              <p className="mt-1 text-xs text-gray-500">
                Usato nell'URL: /courses/{form.slug || 'slug'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrizione breve
              </label>
              <input
                type="text"
                value={form.short_description || ''}
                onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Una breve descrizione del corso"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria
                </label>
                <select
                  value={form.category || ''}
                  onChange={(e) => setForm({ ...form, category: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Nessuna</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Livello
                </label>
                <select
                  value={form.level}
                  onChange={(e) => setForm({ ...form, level: e.target.value as CreateCourseData['level'] })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {levelOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Durata (ore)
              </label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={form.duration_hours || 1}
                onChange={(e) => setForm({ ...form, duration_hours: parseFloat(e.target.value) || 1 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_published}
                  onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Pubblicato</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_public}
                  onChange={(e) => setForm({ ...form, is_public: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  <GlobeAltIcon className="h-4 w-4 inline mr-1" />
                  Pubblico (visibile anche ai Guest)
                </span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_free}
                  onChange={(e) => setForm({ ...form, is_free: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  <CheckBadgeIcon className="h-4 w-4 inline mr-1" />
                  Gratuito
                </span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_premium}
                  onChange={(e) => setForm({ ...form, is_premium: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Corso Premium</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.has_certificate}
                  onChange={(e) => setForm({ ...form, has_certificate: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Rilascia attestato a fine corso</span>
              </label>

              {form.has_certificate && (
                <div className="ml-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Modello attestato</label>
                  <select
                    value={form.certificate_model ?? ''}
                    onChange={(e) => setForm({ ...form, certificate_model: e.target.value ? Number(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">
                      Predefinito{certificateTemplates.find((t) => t.is_default) ? ` (${certificateTemplates.find((t) => t.is_default)!.name})` : ''}
                    </option>
                    {certificateTemplates.map((template) => (
                      <option key={template.id} value={template.id}>{template.name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    I modelli si gestiscono in <Link to="/gestione/attestati" className="text-primary-600 hover:underline">Modelli attestato</Link>.
                  </p>
                </div>
              )}

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!form.sequential_modules}
                  onChange={(e) => setForm({ ...form, sequential_modules: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  Moduli sequenziali (ogni modulo si sblocca completando il precedente)
                </span>
              </label>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowModal(false)}>
            Annulla
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Salvataggio...' : isEditing ? 'Salva modifiche' : 'Crea corso'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Elimina Corso"
      >
        <ModalBody>
          {courseToDelete && (
            <div className="space-y-4">
              <p className="text-gray-600">
                Sei sicuro di voler eliminare il corso{' '}
                <span className="font-semibold">{courseToDelete.title}</span>?
              </p>
              {courseToDelete.enrolled_count > 0 && (
                <p className="text-sm text-red-600">
                  Attenzione: questo corso ha {courseToDelete.enrolled_count} iscritti.
                </p>
              )}
              <p className="text-sm text-gray-500">
                Questa azione è irreversibile.
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
            Annulla
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Eliminazione...' : 'Elimina corso'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
